import React from "react";
import { Link } from "react-router-dom";
import { Loader2, Inbox, Lock, WifiOff, ArrowLeft } from "lucide-react";
import { Button } from "./ui/button";

export const PageHeader = ({ title, subtitle, action, testId, back = true }) => (
  <div className="mb-6 flex flex-wrap items-start justify-between gap-3" data-testid={testId}>
    <div>
      {back && window.history.length > 1 && <button type="button" onClick={() => window.history.back()} className="mb-2 inline-flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground" data-testid="back-button"><ArrowLeft className="h-4 w-4" /> Back</button>}
      <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">{title}</h1>{subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}</div>
    {action}
  </div>
);

export const Card = ({ children, className = "", ...p }) => (
  <div className={`card-r border border-border bg-card p-5 shadow-[0_1px_2px_rgba(24,54,47,0.04)] ${className}`} {...p}>{children}</div>
);

const TONES = {
  draft: "bg-muted text-foreground", initially_submitted: "bg-amber-50 text-[#91661E]", finalized: "bg-[#DEF1E5] text-primary", amended: "bg-[#DEF1E5] text-primary",
  pending: "bg-amber-50 text-[#91661E]", confirmed: "bg-[#DEF1E5] text-primary", needs_review: "bg-amber-50 text-[#91661E]",
  not_ready: "bg-muted text-muted-foreground", queued: "bg-sky-50 text-sky-800", uploading: "bg-sky-50 text-sky-800", partially_delivered: "bg-amber-50 text-[#91661E]",
  delivered: "bg-[#DEF1E5] text-primary", failed: "bg-red-50 text-[#A4473C]", reconnection_required: "bg-red-50 text-[#A4473C]",
  off: "bg-muted text-muted-foreground", listening: "bg-[#DEF1E5] text-primary", paused: "bg-amber-50 text-[#91661E]", processing: "bg-sky-50 text-sky-800", ready: "bg-[#DEF1E5] text-primary", error: "bg-red-50 text-[#A4473C]",
  active: "bg-[#DEF1E5] text-primary", trial: "bg-sky-50 text-sky-800", past_due: "bg-amber-50 text-[#91661E]", canceled: "bg-muted text-muted-foreground", suspended: "bg-red-50 text-[#A4473C]",
  connected: "bg-[#DEF1E5] text-primary", not_connected: "bg-muted text-muted-foreground", resolved: "bg-[#DEF1E5] text-primary",
};
export const Pill = ({ value, prefix = "", testId }) => (
  <span data-testid={testId} className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${TONES[value] || "bg-muted"}`}>
    {prefix}{String(value || "—").replace(/_/g, " ")}
  </span>
);

export const Loading = ({ label = "Loading…" }) => (
  <div className="flex items-center gap-2 py-10 text-sm text-muted-foreground" role="status" data-testid="loading"><Loader2 className="h-4 w-4 animate-spin" /> {label}</div>
);

export const Empty = ({ title, hint, action, icon: Icon = Inbox, testId = "empty-state" }) => (
  <div className="card-r flex flex-col items-center border border-dashed border-border bg-card/60 px-6 py-12 text-center" data-testid={testId}>
    <Icon className="mb-3 h-8 w-8 text-muted-foreground" />
    <p className="font-semibold">{title}</p>
    {hint && <p className="mt-1 max-w-md text-sm text-muted-foreground">{hint}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

export const Denied = ({ what = "this page" }) => (
  <Empty icon={Lock} title="Permission needed" hint={`Your role doesn't include access to ${what}. Ask your agency administrator if you need it.`} testId="permission-denied"
    action={<Button asChild variant="outline"><Link to="/app">Back to dashboard</Link></Button>} />
);

export const Offline = ({ onRetry }) => (
  <Empty icon={WifiOff} title="Connection lost" hint="We couldn't reach the server. Your last saved work is safe." testId="offline-state"
    action={<Button onClick={onRetry}>Try again</Button>} />
);

export const Simulated = ({ children = "Simulated — not connected to a live service" }) => (
  <span className="inline-flex items-center rounded-full border border-dashed border-[#91661E]/50 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-[#91661E]" data-testid="simulated-badge">{children}</span>
);

export const Field = ({ label, hint, children, required }) => (
  <label className="block space-y-1.5 text-sm">
    <span className="font-medium">{label}{required && <span className="text-[#A4473C]"> *</span>}</span>
    {children}
    {hint && <span className="block text-xs text-muted-foreground">{hint}</span>}
  </label>
);

const US = { month: "2-digit", day: "2-digit", year: "numeric" };
export const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { ...US, hour: "numeric", minute: "2-digit" }) : "—");
export const fmtDate = (d) => (d ? new Date(d.length === 10 ? d + "T12:00:00" : d).toLocaleDateString("en-US", US) : "—");
